const API_URL = (import.meta.env["VITE_API_URL"] as string | undefined) ?? "http://localhost:7410";

/** Where the API lives, for things a fetch cannot express — an <img src>, say. */
export const API_ORIGIN = API_URL;

export interface ApiErrorBody {
  error?: { code?: string; message?: string };
  message?: string | string[];
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }

  get isAuth(): boolean {
    return this.status === 401;
  }
}

type Listener = () => void;
const unauthenticatedListeners = new Set<Listener>();

/**
 * A single place to react to an expired session, rather than every screen
 * checking for 401. Sessions have an idle timeout, so this fires during normal
 * use and not only when something has gone wrong.
 */
export function onUnauthenticated(listener: Listener): () => void {
  unauthenticatedListeners.add(listener);
  return () => unauthenticatedListeners.delete(listener);
}

export interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  body?: unknown;
  signal?: AbortSignal;
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? "GET";

  const response = await fetch(`${API_URL}${path}`, {
    method,
    // The session cookie is httpOnly, so this is the only way it travels. It
    // also means every request is subject to the server's origin check.
    credentials: "include",
    /*
     * FormData goes through untouched, and deliberately WITHOUT a Content-Type:
     * multipart needs a boundary parameter that only the browser can generate,
     * so setting the header by hand produces a request the server cannot parse.
     * Stringifying it would be worse still — JSON.stringify(FormData) is "{}",
     * so an upload would arrive as an empty object and fail as "no file".
     */
    ...(options.body === undefined
      ? { headers: {} }
      : options.body instanceof FormData
        ? { headers: {}, body: options.body }
        : {
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(options.body),
          }),
    ...(options.signal === undefined ? {} : { signal: options.signal }),
  });

  if (response.status === 204) return undefined as T;

  const payload: unknown = await response.json().catch(() => ({}));

  if (!response.ok) {
    const error = toApiError(response.status, payload as ApiErrorBody);
    // The login screens handle their own 401s; a 401 anywhere else means the
    // session ended underneath the user.
    if (error.isAuth && !path.startsWith("/api/v1/auth/")) {
      for (const listener of unauthenticatedListeners) listener();
    }
    throw error;
  }

  return payload as T;
}

/**
 * Fetches a file and saves it, rather than navigating to it.
 *
 * A plain link to the API would not carry the session: the cookie is
 * SameSite=Strict and the app and the API are different origins, so a
 * top-level navigation arrives unauthenticated. Fetching keeps the credentials
 * and the origin check, and the blob is handed to the browser afterwards.
 */
export async function apiDownload(path: string, filename: string): Promise<void> {
  const response = await fetch(`${API_URL}${path}`, { credentials: "include" });

  if (!response.ok) {
    const payload: unknown = await response.json().catch(() => ({}));
    const error = toApiError(response.status, payload as ApiErrorBody);
    if (error.isAuth) {
      for (const listener of unauthenticatedListeners) listener();
    }
    throw error;
  }

  const url = URL.createObjectURL(await response.blob());
  try {
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
  } finally {
    // Revoked once the click has been dispatched. Holding it would keep the
    // whole file in memory for as long as the tab is open.
    URL.revokeObjectURL(url);
  }
}

function toApiError(status: number, body: ApiErrorBody): ApiError {
  if (body.error?.code !== undefined) {
    return new ApiError(status, body.error.code, body.error.message ?? "Something went wrong.");
  }

  // Validation failures come back in Nest's own shape, with `message` as an
  // array of every field that failed. Showing the first is more useful than
  // "Bad Request", and showing all of them is noise.
  if (Array.isArray(body.message)) {
    return new ApiError(status, "INVALID_REQUEST", body.message[0] ?? "Check the form and retry.");
  }
  if (typeof body.message === "string") {
    return new ApiError(status, "INVALID_REQUEST", body.message);
  }

  return new ApiError(status, "UNKNOWN", "Something went wrong. Try again.");
}
